<template>
  <generic-modal :title="$t('note')" :is_wide="true" ref="generic_modal">
    <!-- Formulario -->
    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("title") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="meta.title"
              :placeholder="$t('title')"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("content") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.content"
              :placeholder="$t('content')"
              rows="8"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <generic-dropdown
            :title="$t('category')"
            :placeholder="$t('select_category')"
            :is_multiple="false"
            url="/note/category/catalog"
            name_field="name"
            value_field="category_id"
            @selected="category_selected"
            ref="category_dropdown"
          />
        </div>
      </div>
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("priority") }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="meta.priority">
                <option value="low">{{ $t("low_priority") }}</option>
                <option value="medium">{{ $t("medium_priority") }}</option>
                <option value="high">{{ $t("high_priority") }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("status") }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="meta.status">
                <option value="draft">{{ $t("draft") }}</option>
                <option value="published">{{ $t("published") }}</option>
                <option value="archived">{{ $t("archived") }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-half">
        <div class="field">
          <div class="control">
            <label class="checkbox">
              <input type="checkbox" v-model="meta.is_shared" />
              {{ $t("is_shared") }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("tags") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="tags_input"
              :placeholder="$t('add_tags')"
              @keyup.enter="add_tag"
            />
          </div>
          <div
            class="field is-grouped is-grouped-multiline mt-2"
            v-if="meta.tags && meta.tags.length > 0"
          >
            <div class="control" v-for="(tag, index) in meta.tags" :key="index">
              <div class="tags has-addons">
                <span class="tag is-info">{{ tag }}</span>
                <a class="tag is-delete" @click="remove_tag(index)"></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error handling -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer buttons -->
    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="save"
        :disabled="is_form_invalid"
      >
        <span class="icon"><i class="fa fa-save"></i></span>
        <span>{{ $t("save") }}</span>
      </button>
      <button
        class="button is-danger is-rounded"
        @click="remove"
        v-if="meta.uuid"
      >
        <span class="icon"><i class="fa fa-trash"></i></span>
        <span>{{ $t("delete") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import {
  put,
  post,
  del,
  get,
  generate_error_message_text,
  confirm_deletion_dialog,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "NoteModal",
  components: { GenericModal, GenericDropdown },
  data() {
    return {
      meta: {},
      error: "",
      tags_input: "",
    };
  },
  computed: {
    is_form_invalid() {
      return !this.meta.title || !this.meta.content;
    },
  },
  methods: {
    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    show_modal(uuid = null) {
      this.set_initial_state();

      if (uuid) {
        get("/note/note", { uuid })
          .then((note) => {
            this.meta = note;
            this.$refs.generic_modal.show_modal();

            // Establecer categoría en dropdown después de cargar
            if (note.category_id) {
              this.$refs.category_dropdown.set_value(note.category_id);
            }
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      } else {
        this.$refs.generic_modal.show_modal();
      }
    },

    save() {
      const isEdit = !!this.meta.uuid;
      const method = isEdit ? post : put;
      const endpoint = "/note/note";

      method(endpoint, this.meta)
        .then(() => {
          this.$emit("success");
          this.$refs.generic_modal.close_modal();
        })
        .catch((error) => {
          this.error = generate_error_message_text(error);
        });
    },

    remove() {
      confirm_deletion_dialog(this.$t("are_you_sure_delete"), this, () => {
        del("/note/note", { uuid: this.meta.uuid })
          .then(() => {
            this.$emit("success");
            this.$refs.generic_modal.close_modal();
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      });
    },

    category_selected(value) {
      this.meta.category_id = value.category_id;
    },

    add_tag() {
      if (this.tags_input.trim()) {
        if (!this.meta.tags) {
          this.meta.tags = [];
        }
        if (!this.meta.tags.includes(this.tags_input.trim())) {
          this.meta.tags.push(this.tags_input.trim());
        }
        this.tags_input = "";
      }
    },

    remove_tag(index) {
      if (this.meta.tags) {
        this.meta.tags.splice(index, 1);
      }
    },

    set_initial_state() {
      this.meta = {
        title: "",
        content: "",
        category_id: null,
        priority: "medium",
        status: "draft",
        tags: [],
        is_shared: false,
      };
      this.error = "";
      this.tags_input = "";
    },
  },
};
</script>
